import { useEffect, useState } from 'react';
import classNames from 'classnames';
import '/assets/styles/pages/menu-search.scss';
import kebabCase from 'lodash/kebabCase';

// Sections
import MenuHeader from '/views/sections/menu-header';

// Partials
import Btn from '/views/partials/btn';
// import Placeholder from '/views/partials/placeholder';
import Loader from '/views/partials/loader';
import Link from '@wearetla/tla-essentials-tools/partials/link';
import Pricebox from '/views/partials/pricebox';
import EmptyMessage from '/views/partials/empty-message';

// Services
import menuServices from '/services/menu';

// Context
import { useModals } from '@wearetla/tla-essentials-tools/utilities/modals';
import { useNavigator, useParams } from '@wearetla/tla-essentials-tools/utilities/navigator';
import { useBranchSelection } from '/utilities/branch-selection';
import { useTranslation } from '/utilities/i18n';

// Hooks
import useDebounce from '@wearetla/tla-essentials-tools/hooks/debounce';

const searchHistoryPersistenceKey = 'if-sokak-search-history';
const searchHistoryPersistenceSize = 5;

const useSearchHistory = (searchResults) => {
	const [searchHistory, setSearchHistory] = useState(null);

	const clearHistoryItem = (itemIndex) => {
		setSearchHistory(searchHistory.filter((item, nth) => nth !== itemIndex));
	};

	useEffect(() => {
		try {
			const parsedPersistenceData = JSON.parse(
				window.localStorage.getItem(searchHistoryPersistenceKey)
			);

			if (typeof parsedPersistenceData === 'object' && Array.isArray(parsedPersistenceData)) {
				setSearchHistory(parsedPersistenceData);
			} else {
				setSearchHistory([]);
			}
		} catch (e) {
			setSearchHistory([]);
		}
	}, []);

	useEffect(() => {
		if (searchHistory) {
			window.localStorage.setItem(searchHistoryPersistenceKey, JSON.stringify(searchHistory));
		}
	}, [searchHistory]);

	useEffect(() => {
		if (typeof searchHistory === 'object' && searchResults?.query?.length) {
			const searchParamKey = kebabCase(searchResults?.query.trim());
			if (kebabCase(searchHistory?.[0] ?? '') !== searchParamKey) {
				setSearchHistory([
					searchResults.query.trim(),
					...(searchHistory ?? [])
						.slice(0, searchHistoryPersistenceSize)
						.filter((param) => kebabCase(param.trim()) !== searchParamKey),
				]);
			}
		}
	}, [searchResults, searchHistory]);

	return {
		searchHistory,
		clearHistoryItem,
	};
};

const MenuSearch = () => {
	const { openModal } = useModals();
	const { redirect } = useNavigator();
	const { query } = useParams('menuSearch');
	const { selectedBranch } = useBranchSelection();
	const { t } = useTranslation();

	const [searchParam, setSearchParam] = useState(query ?? '');
	const [searchResults, setSearchResults] = useState(null);
	// const [rawSearchSuggestions, setSearchSuggestions] = useState(null);

	const { searchHistory, clearHistoryItem } = useSearchHistory(searchResults);

	const debouncedSearchParam = useDebounce(searchParam, 700, query);

	// const searchSuggestions = useMemo(
	// 	() => rawSearchSuggestions ?? [null, null, null],
	// 	[rawSearchSuggestions]
	// );

	// useEffect(() => {
	// 	menuServices
	// 		.getMenuSearchSuggestions()
	// 		.then(setSearchSuggestions)
	// 		.catch(() => {});
	// }, []);

	useEffect(() => {
		if (query) {
			setSearchParam(query);
		}
	}, [query]);

	useEffect(() => {
		setSearchResults(null);
	}, [searchParam]);

	useEffect(() => {
		redirect(
			'menuSearch',
			{ query: debouncedSearchParam?.length ? debouncedSearchParam : undefined },
			{ replace: true }
		);
		if (selectedBranch?.slug && debouncedSearchParam?.length) {
			menuServices
				.getMenuSearchResults(selectedBranch.slug, debouncedSearchParam)
				.then(setSearchResults)
				.catch((feedback) => {
					openModal('message', { feedback });
					setSearchParam('');
				});
		}
	}, [selectedBranch, debouncedSearchParam]);

	return (
		<>
			<MenuHeader
				autoFocus
				onSearchValueChange={setSearchParam}
				searchValue={searchParam}
				backLink="menu"
			/>
			<div className="search-container loader-container">
				{searchParam ? (
					<div className="search-results wrapper">
						<strong className="results-title">{t('Sonuçlar')}</strong>
						<div className="results-list loader-container">
							<Loader dark loading={!searchResults} />

							{!!searchResults?.results?.length && (
								<>
									{searchResults.results.map((result) => (
										<ResultCard key={result.id} result={result} />
									))}
								</>
							)}
							{searchResults?.results?.length === 0 && (
								<EmptyMessage className="list-emptymessage">{t('Ürün Bulunamadı')}</EmptyMessage>
							)}
						</div>

						{!!searchResults?.suggestions?.length && (
							<div className="results-suggestions">
								<strong className="results-title">{t('Önerilen Sonuçlar')}</strong>

								<div className="results-list">
									{searchResults.suggestions.map((result) => (
										<ResultCard key={result.id} result={result} />
									))}
								</div>
							</div>
						)}
					</div>
				) : (
					<>
						{/* <div className="search-suggested wrapper">
							<strong className="suggested-title">Önerilen Aramalar</strong>

							<div className="suggested-terms">
								{searchSuggestions.map((suggestion, nth) => (
									<Fragment key={suggestion?.term || nth}>
										{suggestion ? (
											<Btn
												onClick={() => {
													setSearchParam(suggestion.term);
												}}
												className="terms-term medium white"
												key={suggestion}
											>
												{suggestion.term}
											</Btn>
										) : (
											<Placeholder light className="terms-term" key={nth} />
										)}
									</Fragment>
								))}
							</div>
						</div> */}
						<Loader dark loading={searchHistory === null} />
						<div className="search-history wrapper">
							{searchHistory?.length ? (
								<>
									<strong className="history-title">{t('Arama Geçmişi')}</strong>

									<div className="history-searches">
										{searchHistory.map((term, nth) => (
											<div className="searches-item" key={nth}>
												<button
													onClick={() => {
														setSearchParam(term);
													}}
													className="item-btn"
												>
													{term}
												</button>
												<Btn
													onClick={() => {
														clearHistoryItem(nth);
													}}
													className="item-removebtn primary medium"
												>
													{t('Sil')}
												</Btn>
											</div>
										))}
									</div>
								</>
							) : (
								<EmptyMessage>
									{t('Arama geçmişin boş, arama kutusuna aramak istediğin ürünü yazarak hemen aramaya başlayabilirsin.')}
								</EmptyMessage>
							)}
						</div>
					</>
				)}
			</div>
		</>
	);
};

const ResultCard = ({ result }) => (
	<Link
		className={classNames('list-result', { 'has-description': !!result.description })}
		href="menuProduct"
		params={{ id: result.id }}
		key={result.id}
	>
		<div className="result-textwrap">
			<strong className="result-title">{result.title}</strong>
			{!!result.description && <p className="result-description">{result.description}</p>}
		</div>
		<Pricebox colored price={result.price} />
	</Link>
);

export default MenuSearch;
